import { IUseQueryProps } from 'store/queries/types';
import { useAppSelector } from 'store/useAppSelector';
import { useAuth } from 'modules/auth/hooks/useAuth';
import { useUser } from 'modules/user/hooks/useUser';

import {
  selectPersonalAccountJWT,
  selectPersonalAccountJWTLoaded,
} from '../store/selectors';
import { useIsOldAccountQuery } from '../actions/hooks/useIsOldAccountQuery';
import { useOAuthAccountJWTQuery } from '../actions/hooks/useOAuthAccountJWTQuery';
import { useOldAccountJWTQuery } from '../actions/hooks/useOldAccountJWTQuery';
import { useStandardAccountJWTQuery } from '../actions/hooks/useStandardAccountJWTQuery';

export interface IUsePersonalAccountJWTProps extends IUseQueryProps {}

export const usePersonalAccountJWT = ({
  skipFetching = false,
}: IUsePersonalAccountJWTProps = {}) => {
  const { authAddress, isLoggedIn, isOAuth, isWeb3Auth } = useAuth();
  const { isWeb3OAuth } = useUser();

  const isLoggedInWeb3User = isLoggedIn && isWeb3Auth;

  const { isOldAccount, loading: isOldAccountLoading } = useIsOldAccountQuery({
    account: authAddress!,
    skipFetching: skipFetching || !isLoggedInWeb3User || !authAddress,
  });

  const { loading: oldAccountJWTLoading } = useOldAccountJWTQuery({
    account: authAddress!,
    skipFetching:
      skipFetching || !isLoggedInWeb3User || !isOldAccount || !authAddress,
  });

  const { loading: standardAccountJWTLoading } = useStandardAccountJWTQuery({
    params: undefined,
    skipFetching:
      skipFetching ||
      (!isLoggedInWeb3User && !isWeb3OAuth) ||
      oldAccountJWTLoading,
  });

  const { loading: oAuthAccountJWTLoading } = useOAuthAccountJWTQuery({
    params: undefined,
    skipFetching: skipFetching || isWeb3Auth || !isOAuth || isWeb3OAuth,
  });

  const personalAccountJWT = useAppSelector(state =>
    selectPersonalAccountJWT(state, authAddress!),
  );

  const loaded = useAppSelector(state =>
    selectPersonalAccountJWTLoaded(state, authAddress!),
  );

  const loading =
    isOldAccountLoading ||
    oldAccountJWTLoading ||
    standardAccountJWTLoading ||
    oAuthAccountJWTLoading;

  return { loaded, loading, personalAccountJWT };
};
